import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Building2, Plus, Sparkles, LogOut, ArrowRight } from 'lucide-react';
import { usePostApiWorkspaces, useGetApiWorkspaces } from '@/api/generated/workspaces/workspaces';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogoIcon } from '@/components/common/Logo';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const createWorkspaceSchema = z.object({
  name: z.string().trim().min(3, 'O nome do workspace deve ter pelo menos 3 caracteres'),
});

type CreateWorkspaceFormValues = z.infer<typeof createWorkspaceSchema>;

export const NoWorkspacesPage: React.FC = () => {
  const [isCreating, setIsCreating] = useState(false);
  const createMutation = usePostApiWorkspaces();
  const { setWorkspaces, setWorkspace } = useWorkspaceStore();
  const { refetch } = useGetApiWorkspaces();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateWorkspaceFormValues>({
    resolver: zodResolver(createWorkspaceSchema),
    defaultValues: {
      name: '',
    },
  });

  const onSubmit = async (data: CreateWorkspaceFormValues) => {
    try {
      const result = await createMutation.mutateAsync({ data });
      toast.success('Workspace criado com sucesso!');

      const { data: workspacesList } = await refetch();
      if (workspacesList) {
        setWorkspaces(workspacesList as any[]);
        const newWorkspace = (result as any)?.id
          ? (workspacesList as any[]).find((w: any) => w.id === (result as any).id)
          : (workspacesList as any[]).find((w: any) => w.name === data.name);

        if (newWorkspace) {
          setWorkspace(newWorkspace.id);
        }
      }

      navigate('/conversations', { replace: true });
    } catch {
      // Global interceptor handles API errors
    }
  };

  const handleLogout = () => {
    logout();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    toast.info('Sessão encerrada');
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen w-full flex flex-col justify-between bg-gradient-to-b from-background to-muted/20 p-4 sm:p-8">
      {/* Top Bar */}
      <header className="flex items-center justify-between max-w-5xl w-full mx-auto">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 flex items-center justify-center rounded-lg bg-primary/10 text-primary">
            <LogoIcon className="h-6 w-6 text-primary" />
          </div>
          <span className="font-bold text-lg tracking-tight">Tales</span>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground hidden sm:inline">
            Conectado como <strong className="text-foreground">{user?.email}</strong>
          </span>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-1.5 text-xs text-muted-foreground hover:text-destructive">
            <LogOut className="h-3.5 w-3.5" />
            <span>Sair</span>
          </Button>
        </div>
      </header>

      {/* Main Empty State Container */}
      <main className="flex-1 flex items-center justify-center my-8">
        <div className="w-full max-w-lg rounded-2xl border bg-card/80 backdrop-blur-sm p-6 sm:p-10 shadow-lg text-center space-y-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-8 ring-primary/5">
            <Building2 className="h-8 w-8" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Nenhum Workspace Encontrado
            </h1>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Você não pertence a nenhum workspace no momento. Crie seu primeiro espaço de trabalho para começar a gerenciar lojas e atendimentos.
            </p>
          </div>

          {!isCreating ? (
            <div className="pt-2">
              <Button
                size="lg"
                onClick={() => setIsCreating(true)}
                className="w-full sm:w-auto min-w-[200px] gap-2 shadow-sm"
              >
                <Plus className="h-4 w-4" />
                <span>Criar Meu Workspace</span>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2 text-left">
              <div className="space-y-1.5">
                <Label htmlFor="workspace-name" className="text-xs font-semibold">
                  Nome do Workspace
                </Label>
                <Input
                  id="workspace-name"
                  placeholder="Ex: Minha Empresa ou Loja Principal"
                  autoFocus
                  {...register('name')}
                />
                {errors.name && (
                  <p className="text-xs text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreating(false)}
                  disabled={isSubmitting}
                  className="w-full sm:w-1/2"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-1/2 gap-1.5"
                >
                  {isSubmitting ? (
                    'Criando...'
                  ) : (
                    <>
                      <span>Criar e Começar</span>
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {/* Value Props / Helper Notes */}
          <div className="pt-6 border-t border-border/50 grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>Conecte suas lojas e gerencie atendimentos com IA autônoma.</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <Building2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>Convide sua equipe com funções seguras de Agente e Administrador.</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center text-xs text-muted-foreground">
        Tales &bull; Plataforma de Atendimento Inteligente para E-commerce
      </footer>
    </div>
  );
};

export default NoWorkspacesPage;
